import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Pagination
} from "@mui/material";
import {Link, useLocation, useNavigate} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { artists } from "../assets/content/artists/artists";
import { routesMap } from "../routes/routesMap";
import {useItemPerPage} from "../hooks/useItemPerPage";
import EnhancedCardImage from "../components/EnhancedCardImage";

export default function Artists({ lang }) {
    const { t } = useTranslation();
    const basePath = routesMap.artists.path[lang];
    // Paginazione
    let itemsPerPage = useItemPerPage({xl: 8, lg: 6, md: 6, sm: 4, xs: 6});
    // Use screen.height (physical screen size) rather than window.innerHeight:
    // on mobile browsers innerHeight changes when the address bar hides/shows
    // while scrolling, which was making the two pagination controls recompute
    // a different item/page count depending on scroll position when clicked.
    if (window.screen.height <= 800) itemsPerPage = itemsPerPage/2;
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const initialPage = parseInt(params.get("page") || "1", 10);
    const [page, setPage] = useState(initialPage);
    const totalPages = Math.ceil(artists.length / itemsPerPage);

    // Keep local page state in sync with the URL (e.g. browser back/forward
    // navigation, or external links setting ?page=).
    useEffect(() => {
        const urlPage = parseInt(params.get("page") || "1", 10);
        if (urlPage !== page) {
            setPage(urlPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Clamp the page to the valid range whenever itemsPerPage changes
    // (e.g. switching between mobile and desktop layouts), so a page number
    // that is no longer valid doesn't render an empty grid.
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            const clampedPage = totalPages;
            setPage(clampedPage);
            const newParams = new URLSearchParams(location.search);
            newParams.set("page", clampedPage);
            navigate({ search: newParams.toString() }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages]);

    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    const currentArtists = artists.slice(start, end);
    const placeholders = (currentArtists.length < itemsPerPage)
        ? Array(itemsPerPage - currentArtists.length).fill(0)
        : [];

    const handlePaginationChange = (event, value) => {
        setPage(value);
        params.set("page", value);
        navigate({ search: params.toString() });
    };

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%"
        }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    my: 2,
                }}
            >
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePaginationChange}
                    color="primary"
                />
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr", xl: "1fr 1fr 1fr 1fr" },
                    alignContent: "start"
                }}
            >
                {currentArtists.map((artist) => (
                    <Card key={artist.name} sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%"
                    }}>
                        <CardActionArea component={Link} to={`${basePath}/${artist.name}`}>
                            <EnhancedCardImage image={artist.image} height={{xs: 250, md: 280, xl: window.innerWidth >= 2560 ? 380 : 280 }}/>
                            <CardContent>
                                <Typography variant="body2">{t(`artists.${artist.titleKey}`)}</Typography>
                                <Typography variant="h6" minHeight={64}>{artist.displayName}</Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
                {placeholders.map((_, idx) => (
                    <Box key={`placeholder-${idx}`} sx={{ visibility: "hidden", width: "100%" }} />
                ))}
            </Box>

            <Box
                sx={{
                    display: { xs: "flex", sm: "none"},
                    justifyContent: "center",
                    mt: 2,
                }}
            >
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePaginationChange}
                    color="primary"
                />
            </Box>
        </Box>
    );
}